import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  useMediaQuery,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Download, People, ChevronLeft, ChevronRight, ExpandMore, ExpandLess } from '@mui/icons-material';
import AppHeader from './AppHeader';
import SessionSelector from './SessionSelector';
import { useClearOnSiteChange, useCurrentSiteId } from '../hooks/useCurrentSiteId';
import { sessionAPI, studentAPI } from '../services/api';
import { flattenGroupedStudents, groupStudentsByClass, normalizeClassNum } from '../utils/groupStudentsByClass';
import {
  clearAttendanceDraft,
  classroomPickerLabel,
  normalizeHhmm,
  nowRoundedToFiveMinutes,
  readAttendanceDraft,
  readLastClassFilter,
  readTouchModePreference,
  recordToUiStatus,
  writeAttendanceDraft,
  writeLastClassFilter,
  writeTouchModePreference,
} from '../utils/attendanceDraft';

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mondayOf = (isoDate) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return fmtDate(d);
};

const shiftWeek = (isoMonday, weeks) => {
  const d = new Date(`${isoMonday}T00:00:00`);
  d.setDate(d.getDate() + weeks * 7);
  return fmtDate(d);
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const AttendancePage = () => {
  const siteId = useCurrentSiteId();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [absenceReasons, setAbsenceReasons] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [serviceType, setServiceType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [absentReport, setAbsentReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [monthlyLogOpen, setMonthlyLogOpen] = useState(false);
  const [monthlyLogMonth, setMonthlyLogMonth] = useState(todayStr().slice(0, 7));
  const [monthlyLogDownloading, setMonthlyLogDownloading] = useState(false);
  const [cpseOpen, setCpseOpen] = useState(false);
  const [cpseMonth, setCpseMonth] = useState(todayStr().slice(0, 7));
  const [cpseBusy, setCpseBusy] = useState(false);
  const [cpseReport, setCpseReport] = useState(null);
  const [cpseImportReport, setCpseImportReport] = useState(null);
  const [aideOpen, setAideOpen] = useState(false);
  const [aideWeekStart, setAideWeekStart] = useState(mondayOf(todayStr()));
  const [aideData, setAideData] = useState(null);
  const [aideEdits, setAideEdits] = useState({});
  const [aideBusy, setAideBusy] = useState(false);
  const [aideMsg, setAideMsg] = useState(null);
  const [aideLogMonth, setAideLogMonth] = useState(todayStr().slice(0, 7));
  // Grouped-by-class is default under All classes; flat keeps whole-school A–Z.
  const [listLayout, setListLayout] = useState('grouped');
  const [classrooms, setClassrooms] = useState([]);
  const [classFilter, setClassFilter] = useState(() => readLastClassFilter());
  const [local, setLocal] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  // Surface Pro reports ≥900px CSS width in both orientations — do NOT key cards off `md`.
  // any-pointer:coarse stays true with a keyboard attached; (pointer:coarse) can flip to fine.
  const prefersCoarsePointer = useMediaQuery('(any-pointer: coarse)');
  const [touchModeOverride, setTouchModeOverride] = useState(() => readTouchModePreference());
  const touchMode = touchModeOverride === null ? prefersCoarsePointer : touchModeOverride;
  const [notesOpen, setNotesOpen] = useState({});
  const classSelected = Boolean(classFilter);
  const dirtyCount = Object.keys(dirty).length;
  const hasDirty = dirtyCount > 0;
  const classNavOptions = useMemo(
    () => classrooms.map((c) => c.class_num).filter(Boolean),
    [classrooms],
  );
  const currentClassIndex = classNavOptions.indexOf(classFilter);
  const currentClassroom = useMemo(
    () => classrooms.find((c) => c.class_num === classFilter) || null,
    [classrooms, classFilter],
  );
  const canNavClasses = currentClassIndex >= 0;

  const setTouchMode = (enabled) => {
    setTouchModeOverride(Boolean(enabled));
    writeTouchModePreference(Boolean(enabled));
  };

  const confirmDiscardIfDirty = useCallback(() => {
    if (!hasDirty) return true;
    return window.confirm(
      `You have ${dirtyCount} unsaved mark(s). Switch anyway and discard them?`,
    );
  }, [hasDirty, dirtyCount]);

  const loadSessions = async () => {
    try {
      const res = await sessionAPI.getSessions();
      setSessions(res.data.results || res.data || []);
      let cur = null;
      try {
        const curRes = await sessionAPI.getCurrentSession();
        cur = curRes.data;
      } catch (_) {}
      setCurrentSession(cur);
    } catch (e) {
      setError(e.message);
    }
  };

  useClearOnSiteChange(siteId, () => {
    setSessions([]);
    setCurrentSession(null);
    setStudents([]);
    setRecords({});
    setClassrooms([]);
  });

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    if (!currentSession?.id) return;
    sessionAPI.getAbsenceReasons().then((res) => setAbsenceReasons(res.data.results || res.data || [])).catch(() => {});
    sessionAPI.getClassrooms(currentSession.id)
      .then((res) => {
        const list = res.data.results || res.data || [];
        setClassrooms([...list].sort((a, b) =>
          String(a.class_num || '').localeCompare(String(b.class_num || ''), undefined, { numeric: true, sensitivity: 'base' }),
        ));
      })
      .catch(() => setClassrooms([]));
  }, [currentSession?.id]);

  // Persist dirty marks so a dropped connection / locked tablet does not lose the class.
  // Only write when there is something to keep — never wipe a draft on empty mount.
  useEffect(() => {
    if (!currentSession?.id || !date) return;
    if (!Object.keys(dirty).length) return;
    writeAttendanceDraft(currentSession.id, date, classFilter || 'all', local, dirty);
  }, [currentSession?.id, date, classFilter, local, dirty]);

  useEffect(() => {
    let cancelled = false;
    if (!currentSession?.id) {
      setStudents([]);
      setRecords({});
      return () => { cancelled = true; };
    }
    setLoading(true);
    const params = { session: currentSession.id, page_size: 5000, discharge: 'active' };
    if (serviceType) params.service_type = serviceType;
    if (classFilter) params.class_num = classFilter;
    studentAPI.getStudents(currentSession.id, params)
      .then((res) => {
        if (cancelled) return;
        setStudents(res.data.results || res.data || []);
        setPage(0);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentSession?.id, serviceType, classFilter]);

  useEffect(() => {
    if (!currentSession?.id || !date) {
      setRecords({});
      return;
    }
    sessionAPI.getAttendance({ session: currentSession.id, date })
      .then((res) => {
        const list = res.data.results || res.data || [];
        const byStudent = {};
        list.forEach((r) => { byStudent[r.student] = r; });
        setRecords(byStudent);
        const draft = readAttendanceDraft(currentSession.id, date, classFilter || 'all');
        if (draft && Object.keys(draft.dirty || {}).length) {
          setLocal(draft.local || {});
          setDirty(draft.dirty || {});
        } else {
          setLocal({});
          setDirty({});
        }
      })
      .catch(() => setRecords({}));
  }, [currentSession?.id, date, classFilter]);

  const handleSessionChange = async (sessionId) => {
    if (!sessionId) return;
    if (!confirmDiscardIfDirty()) return;
    try {
      if (currentSession?.id) clearAttendanceDraft(currentSession.id, date, classFilter || 'all');
      await sessionAPI.setActiveSession(sessionId);
      const res = await sessionAPI.getCurrentSession();
      setLocal({});
      setDirty({});
      setCurrentSession(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const changeDate = (next) => {
    if (!next || next === date) return;
    if (!confirmDiscardIfDirty()) return;
    if (currentSession?.id) clearAttendanceDraft(currentSession.id, date, classFilter || 'all');
    setLocal({});
    setDirty({});
    setDate(next);
  };

  const changeClassFilter = (next) => {
    if (next === classFilter) return;
    if (!confirmDiscardIfDirty()) return;
    if (currentSession?.id) clearAttendanceDraft(currentSession.id, date, classFilter || 'all');
    setLocal({});
    setDirty({});
    setClassFilter(next);
    writeLastClassFilter(next);
    setPage(0);
  };

  const getUiStatus = (studentId) => {
    if (local[studentId] !== undefined) return local[studentId];
    return recordToUiStatus(records[studentId]);
  };

  const getUiReason = (studentId) => {
    const key = `reason_${studentId}`;
    if (local[key] !== undefined) return local[key];
    const base = records[studentId]?.absence_reason;
    if (base) return base;
    return (
      absenceReasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code
      || absenceReasons[0]?.reason_code
      || ''
    );
  };

  const getUiLateTime = (studentId) => {
    const key = `late_${studentId}`;
    if (local[key] !== undefined) return local[key];
    return normalizeHhmm(records[studentId]?.late_arrival_time);
  };

  const getUiEarlyTime = (studentId) => {
    const key = `early_${studentId}`;
    if (local[key] !== undefined) return local[key];
    return normalizeHhmm(records[studentId]?.early_dismissal_time);
  };

  const getUiNotes = (studentId) => {
    const key = `notes_${studentId}`;
    if (local[key] !== undefined) return local[key];
    return records[studentId]?.notes || '';
  };

  const markStudentDirty = (studentId, patch) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    setDirty((prev) => ({ ...prev, [studentId]: true }));
    setSuccess(null);
  };

  const setStudentStatus = (studentId, status) => {
    const patch = { [studentId]: status };
    if (status === 'absent' || status === '') {
      patch[`late_${studentId}`] = '';
      patch[`early_${studentId}`] = '';
    }
    markStudentDirty(studentId, patch);
  };

  const setStudentReason = (studentId, reasonCode) => {
    markStudentDirty(studentId, { [`reason_${studentId}`]: reasonCode });
  };

  const setLateChecked = (studentId, checked) => {
    markStudentDirty(studentId, {
      [`late_${studentId}`]: checked ? (getUiLateTime(studentId) || nowRoundedToFiveMinutes()) : '',
    });
  };

  const setEarlyChecked = (studentId, checked) => {
    markStudentDirty(studentId, {
      [`early_${studentId}`]: checked ? (getUiEarlyTime(studentId) || nowRoundedToFiveMinutes()) : '',
    });
  };

  const setLateTime = (studentId, value) => {
    markStudentDirty(studentId, { [`late_${studentId}`]: value || '' });
  };

  const setEarlyTime = (studentId, value) => {
    markStudentDirty(studentId, { [`early_${studentId}`]: value || '' });
  };

  const setStudentNotes = (studentId, value) => {
    markStudentDirty(studentId, { [`notes_${studentId}`]: value });
  };

  const markAllPresent = () => {
    // Scoped to the currently loaded roster (selected class when a class is picked).
    const nextLocal = { ...local };
    const nextDirty = { ...dirty };
    students.forEach((s) => {
      nextLocal[s.id] = 'present';
      nextDirty[s.id] = true;
    });
    setLocal(nextLocal);
    setDirty(nextDirty);
    setSuccess(null);
  };

  const handleSaveAttendance = async () => {
    if (!hasDirty || !currentSession?.id) return false;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const payloadRecords = [];
    Object.keys(dirty).forEach((sid) => {
      const id = Number(sid);
      let st = getUiStatus(id);
      let reason = getUiReason(id);
      if (st === 'absent' && !reason) {
        reason = absenceReasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code
          || absenceReasons[0]?.reason_code;
      }
      const late = st === 'present' ? (getUiLateTime(id) || null) : null;
      const early = st === 'present' ? (getUiEarlyTime(id) || null) : null;
      payloadRecords.push({
        student: id,
        status: st,
        absence_reason: st === 'absent' ? reason : null,
        late_arrival_time: late,
        early_dismissal_time: early,
        notes: getUiNotes(id) || '',
      });
    });
    try {
      await sessionAPI.bulkSetAttendance({ date, records: payloadRecords });
      const res = await sessionAPI.getAttendance({ session: currentSession.id, date });
      const list = res.data.results || res.data || [];
      const byStudent = {};
      list.forEach((r) => { byStudent[r.student] = r; });
      setRecords(byStudent);
      setLocal({});
      setDirty({});
      clearAttendanceDraft(currentSession.id, date, classFilter || 'all');
      setSuccess(`Saved ${payloadRecords.length} mark(s).`);
      return true;
    } catch (e) {
      // Keep dirty state — never clear local marks except on confirmed success.
      setError(e.response?.data?.detail || e.message || 'Save failed — marks kept. Retry.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goAdjacentClass = (delta) => {
    if (!canNavClasses) return;
    const nextIdx = currentClassIndex + delta;
    if (nextIdx < 0 || nextIdx >= classNavOptions.length) return;
    changeClassFilter(classNavOptions[nextIdx]);
  };

  const handleSaveAndNextClass = async () => {
    if (hasDirty) {
      const ok = await handleSaveAttendance();
      if (!ok) return;
    }
    if (!canNavClasses || currentClassIndex >= classNavOptions.length - 1) return;
    const next = classNavOptions[currentClassIndex + 1];
    if (currentSession?.id) clearAttendanceDraft(currentSession.id, date, classFilter || 'all');
    setLocal({});
    setDirty({});
    setClassFilter(next);
    writeLastClassFilter(next);
    setPage(0);
  };

  const openDailyAbsentReport = async () => {
    try {
      const res = await sessionAPI.getAttendanceDailyAbsentReport({ date, session: currentSession?.id });
      setAbsentReport(res.data);
      setReportOpen(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const exportDailyAbsentCsv = async () => {
    try {
      const res = await sessionAPI.getAttendanceDailyAbsentReportCsv({ date, session: currentSession?.id });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily_absent_${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const downloadMonthlyAttendanceLogPdf = async () => {
    const [y, m] = monthlyLogMonth.split('-').map(Number);
    setMonthlyLogDownloading(true);
    try {
      const res = await sessionAPI.getMonthlyAttendanceLogPdf({
        session: currentSession?.id,
        month: m,
        year: y,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monthly_attendance_log_${y}_${String(m).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setMonthlyLogOpen(false);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await e.response.data.text());
          setError(parsed.detail || 'Download failed.');
        } catch (_) {
          setError('Download failed.');
        }
      } else {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setMonthlyLogDownloading(false);
    }
  };

  const checkCpseExport = async () => {
    const [y, m] = cpseMonth.split('-').map(Number);
    setCpseBusy(true);
    setCpseReport(null);
    try {
      const res = await sessionAPI.getCpseAttendanceExportReport({ session: currentSession?.id, month: m, year: y });
      setCpseReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCpseBusy(false);
    }
  };

  const downloadCpseXml = async (force = false) => {
    const [y, m] = cpseMonth.split('-').map(Number);
    setCpseBusy(true);
    try {
      const params = { session: currentSession?.id, month: m, year: y };
      if (force) params.force = '1';
      const res = await sessionAPI.getCpseAttendanceExportXml(params);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cpse_attendance_${y}_${String(m).padStart(2, '0')}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Blob error responses need to be read as text to surface the report detail
      if (e.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await e.response.data.text());
          setCpseReport(parsed);
          setError(parsed.detail || 'Export blocked — see issues below.');
        } catch (_) {
          setError('Export failed.');
        }
      } else {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setCpseBusy(false);
    }
  };

  const importCpseFile = async (file) => {
    if (!file) return;
    setCpseBusy(true);
    setCpseImportReport(null);
    try {
      const res = await sessionAPI.importCpseEnrollments(currentSession?.id, file);
      setCpseImportReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCpseBusy(false);
    }
  };

  const loadAideWeek = async (weekStart = aideWeekStart) => {
    if (!currentSession?.id) return;
    setAideBusy(true);
    setAideMsg(null);
    try {
      const res = await sessionAPI.getCpseAideWeek({ session: currentSession.id, week_start: weekStart });
      setAideData(res.data);
      // Nurse rows: pre-fill empty present-day cells with the agency name (confirm/override, saved on Save Week)
      const prefills = {};
      (res.data.rows || []).forEach((row) => {
        if (row.subtype !== 'nurse' || !row.provider_name) return;
        (res.data.dates || []).forEach((d) => {
          const cell = row.assignments[d] || {};
          if (!cell.staff_name && cell.child_present !== false && !res.data.off_days[d]) {
            prefills[`${row.enrollment_id}|${d}`] = row.provider_name;
          }
        });
      });
      setAideEdits(prefills);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAideBusy(false);
    }
  };

  useEffect(() => {
    if (aideOpen && currentSession?.id) loadAideWeek(aideWeekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aideOpen, aideWeekStart, currentSession?.id]);

  const saveAideWeek = async () => {
    if (!aideData) return;
    setAideBusy(true);
    setAideMsg(null);
    try {
      let saves = 0;
      for (const row of aideData.rows || []) {
        // group changed dates by staff name (bulk-set takes one name per call)
        const byName = {};
        for (const d of aideData.dates || []) {
          const key = `${row.enrollment_id}|${d}`;
          if (!(key in aideEdits)) continue;
          const cell = row.assignments[d] || {};
          if (cell.child_present === false || aideData.off_days[d]) continue;
          const name = (aideEdits[key] || '').trim();
          if (!name || name === cell.staff_name) continue;
          (byName[name] = byName[name] || []).push(d);
        }
        for (const [name, dates] of Object.entries(byName)) {
          await sessionAPI.bulkSetCpseAideAssignments({
            enrollment: row.enrollment_id,
            staff_name: name,
            credential: row.subtype === 'nurse' ? (row.credential || '') : '',
            dates,
          });
          saves += dates.length;
        }
      }
      setAideMsg(saves ? `Saved ${saves} day(s).` : 'Nothing to save.');
      await loadAideWeek(aideWeekStart);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAideBusy(false);
    }
  };

  const downloadAideLogXlsx = async () => {
    setAideBusy(true);
    try {
      const res = await sessionAPI.getCpseAideLogXlsx({ session: currentSession?.id, month: aideLogMonth });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aide_log_${aideLogMonth.replace('-', '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAideBusy(false);
    }
  };

  const cpseBlockingIssues = cpseReport ? [
    ...(cpseReport.students_missing_esid || []).map((r) => `No ESID: ${r.student_name}`),
    ...(cpseReport.classrooms_missing_identifier || []).map((r) => `Classroom "${r.classroom}" missing teacher NPI/ID${r.teacher ? ` (${r.teacher})` : ''}`),
    ...(cpseReport.students_without_classroom || []).map((r) => `No classroom: ${r.student_name}`),
    ...(cpseReport.missing_attendance || []).map((r) => `Missing attendance: ${r.student_name} — ${r.date}`),
    ...(cpseReport.missing_aide_assignment || []).map((r) => `No ${r.subtype === 'nurse' ? 'nurse' : 'aide'} recorded: ${r.student_name} — ${r.date} (${r.esid})`),
  ] : [];

  const orderedStudents = useMemo(() => {
    // With a class selected, A–Z within that class; under All classes keep By class / flat toggle.
    if (classSelected || listLayout !== 'grouped') {
      return [...students].sort((a, b) => {
        const ln = (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' });
        if (ln !== 0) return ln;
        return (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' });
      });
    }
    return flattenGroupedStudents(groupStudentsByClass(students));
  }, [students, listLayout, classSelected]);

  const displayStudents = classSelected
    ? orderedStudents
    : orderedStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderStatusSelect = (studentId, st, { fullWidth = false } = {}) => (
    <FormControl size="small" sx={{ minWidth: fullWidth ? '100%' : 140, ...(fullWidth ? {} : {}) }}>
      <InputLabel id={`status-${studentId}`}>Status</InputLabel>
      <Select
        labelId={`status-${studentId}`}
        label="Status"
        value={st || ''}
        onChange={(e) => setStudentStatus(studentId, e.target.value)}
        sx={{ minHeight: 44, fontSize: '1rem', fontWeight: 600 }}
      >
        <MenuItem value="">—</MenuItem>
        <MenuItem value="present">Present</MenuItem>
        <MenuItem value="absent">Absent</MenuItem>
      </Select>
    </FormControl>
  );

  const renderLateEarlyControls = (studentId, st, { stacked = false } = {}) => {
    if (st !== 'present') return null;
    const lateTime = getUiLateTime(studentId);
    const earlyTime = getUiEarlyTime(studentId);
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: stacked ? 0.5 : 2,
          alignItems: stacked ? 'stretch' : 'center',
          mt: stacked ? 1 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={Boolean(lateTime)}
                onChange={(e) => setLateChecked(studentId, e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
              />
            )}
            label="Late arrival"
            sx={{ mr: 0, userSelect: 'none' }}
          />
          {lateTime ? (
            <TextField
              type="time"
              size="small"
              value={lateTime}
              onChange={(e) => setLateTime(studentId, e.target.value)}
              inputProps={{ step: 300 }}
              sx={{ width: 130 }}
            />
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={Boolean(earlyTime)}
                onChange={(e) => setEarlyChecked(studentId, e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
              />
            )}
            label="Early dismissal"
            sx={{ mr: 0, userSelect: 'none' }}
          />
          {earlyTime ? (
            <TextField
              type="time"
              size="small"
              value={earlyTime}
              onChange={(e) => setEarlyTime(studentId, e.target.value)}
              inputProps={{ step: 300 }}
              sx={{ width: 130 }}
            />
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderNotesControl = (studentId, { alwaysOpen = false } = {}) => {
    const notes = getUiNotes(studentId);
    // Collapsed by default; ● indicates an existing note without forcing the row open.
    const open = alwaysOpen || !!notesOpen[studentId];
    return (
      <Box sx={{ mt: alwaysOpen ? 0 : 1 }}>
        {!alwaysOpen && (
          <Button
            size="small"
            onClick={() => setNotesOpen((prev) => ({ ...prev, [studentId]: !open }))}
            endIcon={open ? <ExpandLess /> : <ExpandMore />}
            sx={{ minHeight: 40, textTransform: 'none' }}
          >
            Notes{notes ? ' ●' : ''}
          </Button>
        )}
        <Collapse in={open || alwaysOpen}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Notes for this child"
            value={notes}
            onChange={(e) => setStudentNotes(studentId, e.target.value)}
            sx={{ mt: alwaysOpen ? 0 : 0.5 }}
          />
        </Collapse>
      </Box>
    );
  };

  const renderTabletCard = (s) => {
    const st = getUiStatus(s.id);
    const reason = getUiReason(s.id);
    const showReason = st === 'absent';
    const isDirty = !!dirty[s.id];
    return (
      <Box
        key={s.id}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: isDirty ? 'primary.main' : 'divider',
          borderLeftWidth: isDirty ? 4 : 1,
          boxShadow: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 0, flex: 1, fontSize: '1.1rem' }}>
            {s.first_name} {s.last_name}
          </Typography>
          <Box sx={{ minWidth: 160, flex: '0 0 160px' }}>
            {renderStatusSelect(s.id, st, { fullWidth: true })}
          </Box>
        </Box>
        {showReason && (
          <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
            <InputLabel id={`reason-${s.id}`}>Reason</InputLabel>
            <Select
              labelId={`reason-${s.id}`}
              label="Reason"
              value={reason || ''}
              onChange={(e) => setStudentReason(s.id, e.target.value)}
              required
              sx={{ minHeight: 44 }}
            >
              {absenceReasons.map((r) => (
                <MenuItem key={r.reason_code} value={r.reason_code}>{r.reason_label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {renderLateEarlyControls(s.id, st, { stacked: true })}
        {renderNotesControl(s.id)}
      </Box>
    );
  };

  const renderDesktopRow = (s) => {
    const st = getUiStatus(s.id);
    const reason = getUiReason(s.id);
    const showReason = st === 'absent';
    const isDirty = !!dirty[s.id];
    return (
      <TableRow
        key={s.id}
        sx={{
          bgcolor: isDirty ? 'action.hover' : undefined,
          borderLeft: isDirty ? 4 : 0,
          borderLeftColor: 'primary.main',
          verticalAlign: 'top',
        }}
      >
        <TableCell>{s.last_name}, {s.first_name}</TableCell>
        <TableCell sx={{ minWidth: 150 }}>{renderStatusSelect(s.id, st)}</TableCell>
        <TableCell sx={{ minWidth: 280 }}>{renderLateEarlyControls(s.id, st)}</TableCell>
        <TableCell sx={{ minWidth: 180 }}>{renderNotesControl(s.id, { alwaysOpen: true })}</TableCell>
        <TableCell>
          {showReason ? (
            <Select
              size="small"
              sx={{ minWidth: 140 }}
              value={reason || ''}
              displayEmpty
              onChange={(e) => setStudentReason(s.id, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {absenceReasons.map((r) => (
                <MenuItem key={r.reason_code} value={r.reason_code}>{r.reason_label}</MenuItem>
              ))}
            </Select>
          ) : '—'}
        </TableCell>
        <TableCell>
          {isDirty ? (
            <Typography variant="caption" color="primary.main" fontWeight={600}>Unsaved</Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">Saved</Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderGroupedBody = () => {
    const rows = [];
    let lastClassKey = null;
    displayStudents.forEach((s) => {
      const classNum = normalizeClassNum(s.class_num);
      const classKey = classNum || '__unassigned__';
      if (!classSelected && listLayout === 'grouped' && classKey !== lastClassKey) {
        lastClassKey = classKey;
        const label = classNum || 'Unassigned';
        if (touchMode) {
          rows.push(
            <Typography key={`hdr-${classKey}`} variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
              {label}
            </Typography>,
          );
        } else {
          rows.push(
            <TableRow key={`hdr-${classKey}-${s.id}`} sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={6} sx={{ py: 1 }}>
                <Typography variant="subtitle2" component="span">{label}</Typography>
              </TableCell>
            </TableRow>,
          );
        }
      }
      rows.push(touchMode ? renderTabletCard(s) : renderDesktopRow(s));
    });
    return rows;
  };

  return (
    <Box sx={{ pb: 12 }}>
      <AppHeader backTo="/" rightContent={<Typography variant="subtitle1">Attendance</Typography>} />
      <Box sx={{ p: 2 }}>
        <SessionSelector sessions={sessions} currentSession={currentSession} onSessionChange={handleSessionChange} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => changeDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Class</InputLabel>
            <Select
              value={classFilter}
              label="Class"
              onChange={(e) => changeClassFilter(e.target.value)}
              renderValue={(selected) => {
                if (!selected) return 'All classes';
                if (selected === '__unassigned__') return 'Unassigned';
                const c = classrooms.find((x) => x.class_num === selected);
                return classroomPickerLabel(c) || selected;
              }}
            >
              <MenuItem value="">All classes</MenuItem>
              {classrooms.map((c) => (
                <MenuItem key={c.id} value={c.class_num}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body2">{classroomPickerLabel(c)}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.class_num}</Typography>
                  </Box>
                </MenuItem>
              ))}
              <MenuItem value="__unassigned__">Unassigned</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Service type</InputLabel>
            <Select value={serviceType} label="Service type" onChange={(e) => { setServiceType(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="center_based">Center-based</MenuItem>
              <MenuItem value="related_service">Related service</MenuItem>
            </Select>
          </FormControl>
          {!classSelected && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={listLayout}
              onChange={(_, v) => { if (v != null) { setListLayout(v); setPage(0); } }}
              aria-label="Attendance list layout"
            >
              <ToggleButton value="grouped">By class</ToggleButton>
              <ToggleButton value="flat">A–Z schoolwide</ToggleButton>
            </ToggleButtonGroup>
          )}
          <FormControlLabel
            control={(
              <Switch
                checked={touchMode}
                onChange={(e) => setTouchMode(e.target.checked)}
                inputProps={{ 'aria-label': 'Touch mode card layout' }}
              />
            )}
            label="Touch mode"
            sx={{ ml: 0.5, mr: 0.5, userSelect: 'none' }}
          />
          <Button variant="outlined" onClick={markAllPresent} disabled={!currentSession || loading || !students.length}>
            Mark all present{classSelected ? ' (this class)' : ''}
          </Button>
          <Button variant="outlined" startIcon={<People />} onClick={openDailyAbsentReport} disabled={!currentSession}>
            Daily absent report
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={() => setMonthlyLogOpen(true)} disabled={!currentSession}>
            Monthly Attendance Log
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={() => { setCpseReport(null); setCpseImportReport(null); setCpseOpen(true); }} disabled={!currentSession}>
            CPSE Portal Upload
          </Button>
          <Button variant="outlined" startIcon={<People />} onClick={() => { setAideMsg(null); setAideOpen(true); }} disabled={!currentSession}>
            1:1 Aide Log
          </Button>
          {reportOpen && absentReport && (
            <Button variant="outlined" startIcon={<Download />} onClick={exportDailyAbsentCsv}>
              Export absent CSV
            </Button>
          )}
        </Box>

        <Dialog open={monthlyLogOpen} onClose={() => !monthlyLogDownloading && setMonthlyLogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Monthly Attendance Log</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              One PDF for every class at this site (same layout as the paper monthly log).
              Student last names print as the first initial only.
            </Typography>
            <TextField
              label="Month"
              type="month"
              size="small"
              fullWidth
              value={monthlyLogMonth}
              onChange={(e) => setMonthlyLogMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !monthlyLogDownloading && setMonthlyLogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={downloadMonthlyAttendanceLogPdf} disabled={monthlyLogDownloading}>
              {monthlyLogDownloading ? 'Downloading…' : 'Download PDF'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={cpseOpen} onClose={() => !cpseBusy && setCpseOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>CPSE Portal attendance upload (XML)</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generates the center-based (CB) classroom attendance file for upload at CPSE Portal.
              Every student needs an ESID and every classroom needs a teacher NPI / CPSE ID.
            </Typography>
            <TextField
              label="Month"
              type="month"
              size="small"
              value={cpseMonth}
              onChange={(e) => { setCpseMonth(e.target.value); setCpseReport(null); }}
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 2 }}
            />
            <Button variant="outlined" onClick={checkCpseExport} disabled={cpseBusy || !cpseMonth}>
              Check readiness
            </Button>
            {cpseReport && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  School days: {cpseReport.school_days} · Rows: {cpseReport.rows} (present {cpseReport.present}, absent {cpseReport.absent}, closed {cpseReport.closed}) · Students: {cpseReport.students_included}
                </Typography>
                {cpseBlockingIssues.length > 0 ? (
                  <Alert severity="warning" sx={{ mt: 1, maxHeight: 220, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cpseBlockingIssues.length} issue(s) — affected rows will be left out of the file:
                    </Typography>
                    {cpseBlockingIssues.slice(0, 50).map((msg, i) => (
                      <Typography key={i} variant="body2">• {msg}</Typography>
                    ))}
                    {cpseBlockingIssues.length > 50 && (
                      <Typography variant="body2">…and {cpseBlockingIssues.length - 50} more</Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mt: 1 }}>Ready — no issues found.</Alert>
                )}
              </Box>
            )}
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2">Import ESIDs from CPSE Portal</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Upload the enrollment export (CSV or Excel) from CPSE Portal to fill in student ESIDs and counties.
              </Typography>
              <Button variant="outlined" component="label" disabled={cpseBusy}>
                Upload enrollment file
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { importCpseFile(e.target.files?.[0]); e.target.value = ''; }} />
              </Button>
              {cpseImportReport && (
                <Alert severity={cpseImportReport.unmatched?.length ? 'warning' : 'success'} sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="body2">
                    Created {cpseImportReport.created?.length ?? 0}, updated {cpseImportReport.updated?.length ?? 0}, unmatched {cpseImportReport.unmatched?.length ?? 0}.
                  </Typography>
                  {(cpseImportReport.unmatched || []).map((row, i) => (
                    <Typography key={i} variant="body2">• Not matched: {row.name} ({row.esid})</Typography>
                  ))}
                  {(cpseImportReport.warnings || []).map((row, i) => (
                    <Typography key={`w${i}`} variant="body2">• {row.name}: {row.reason}</Typography>
                  ))}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !cpseBusy && setCpseOpen(false)}>Close</Button>
            {cpseReport && cpseBlockingIssues.length > 0 && (
              <Button color="warning" onClick={() => downloadCpseXml(true)} disabled={cpseBusy}>
                Download anyway
              </Button>
            )}
            <Button variant="contained" onClick={() => downloadCpseXml(false)} disabled={cpseBusy || !cpseMonth}>
              {cpseBusy ? 'Working…' : 'Download XML'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={aideOpen} onClose={() => !aideBusy && setAideOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>1:1 Aide/Nurse Log</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <Button size="small" onClick={() => setAideWeekStart(shiftWeek(aideWeekStart, -1))} disabled={aideBusy}>‹ Prev week</Button>
              <TextField
                label="Week of"
                type="date"
                size="small"
                value={aideWeekStart}
                onChange={(e) => e.target.value && setAideWeekStart(mondayOf(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
              <Button size="small" onClick={() => setAideWeekStart(shiftWeek(aideWeekStart, 1))} disabled={aideBusy}>Next week ›</Button>
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                label="Report month"
                type="month"
                size="small"
                value={aideLogMonth}
                onChange={(e) => setAideLogMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="outlined" startIcon={<Download />} onClick={downloadAideLogXlsx} disabled={aideBusy || !aideLogMonth}>
                Download Monthly Log
              </Button>
            </Box>
            {aideMsg && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setAideMsg(null)}>{aideMsg}</Alert>}
            {aideBusy && !aideData && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {aideData && (aideData.rows || []).length === 0 && (
              <Typography color="text.secondary">No students with aide or nurse enrollments in this session.</Typography>
            )}
            {aideData && (aideData.rows || []).length > 0 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 220 }}>Student</TableCell>
                      {(aideData.dates || []).map((d, i) => (
                        <TableCell key={d} align="center" sx={{ minWidth: 130 }}>
                          {WEEKDAY_LABELS[i]} {d.slice(5).replace('-', '/')}
                          {aideData.off_days[d] && (
                            <Typography variant="caption" display="block" color="text.secondary">{aideData.off_days[d]}</Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aideData.rows.map((row) => (
                      <TableRow key={row.enrollment_id}>
                        <TableCell>
                          {row.student_name}{' '}
                          <Chip
                            size="small"
                            label={row.subtype === 'nurse' ? `Nurse${row.credential ? ` · ${row.credential}` : ''}` : 'Aide'}
                            color={row.subtype === 'nurse' ? 'info' : 'default'}
                            variant="outlined"
                          />
                          {row.provider_name && (
                            <Typography variant="caption" display="block" color="text.secondary">{row.provider_name}</Typography>
                          )}
                        </TableCell>
                        {(aideData.dates || []).map((d) => {
                          const key = `${row.enrollment_id}|${d}`;
                          const cell = row.assignments[d] || {};
                          if (aideData.off_days[d]) {
                            return <TableCell key={d} align="center" sx={{ bgcolor: 'grey.100' }}>—</TableCell>;
                          }
                          if (cell.child_present === false) {
                            return (
                              <TableCell key={d} align="center" sx={{ bgcolor: 'grey.200' }}>
                                <Typography variant="caption" color="text.secondary">Child absent</Typography>
                              </TableCell>
                            );
                          }
                          const value = key in aideEdits ? aideEdits[key] : (cell.staff_name || '');
                          return (
                            <TableCell key={d} sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={value}
                                placeholder={cell.child_present == null ? 'no attendance yet' : ''}
                                onChange={(e) => setAideEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                                sx={{ bgcolor: value.trim() ? undefined : '#fff9c4' }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Yellow = child present but no aide recorded (blocks the CPSE upload). Gray = child absent or school closed — no entry needed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !aideBusy && setAideOpen(false)}>Close</Button>
            <Button variant="contained" onClick={saveAideWeek} disabled={aideBusy || !aideData}>
              {aideBusy ? 'Working…' : 'Save Week'}
            </Button>
          </DialogActions>
        </Dialog>
        {touchMode && canNavClasses && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 2,
              position: 'sticky',
              top: 0,
              zIndex: 8,
              bgcolor: 'background.default',
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <IconButton
              onClick={() => goAdjacentClass(-1)}
              disabled={currentClassIndex <= 0}
              aria-label="Previous class"
              size="large"
              sx={{ minWidth: 48, minHeight: 48 }}
            >
              <ChevronLeft />
            </IconButton>
            <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.15rem' }} noWrap>
                {classroomPickerLabel(currentClassroom) || classFilter}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {classFilter}
                {classNavOptions.length > 0
                  ? ` · ${currentClassIndex + 1} of ${classNavOptions.length}`
                  : ''}
              </Typography>
            </Box>
            <IconButton
              onClick={() => goAdjacentClass(1)}
              disabled={currentClassIndex >= classNavOptions.length - 1}
              aria-label="Next class"
              size="large"
              sx={{ minWidth: 48, minHeight: 48 }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
        {hasDirty && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {dirtyCount} unsaved mark(s) — tap Save attendance before leaving this class.
          </Alert>
        )}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {!loading && students.length > 0 && touchMode && (
          <Box sx={{ mt: 2, maxWidth: '100%', overflowX: 'hidden' }}>
            {listLayout === 'grouped' || classSelected
              ? renderGroupedBody()
              : displayStudents.map((s) => renderTabletCard(s))}
          </Box>
        )}
        {!loading && students.length > 0 && !touchMode && (
          <>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name (Last, First)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Late / Early</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Absence reason</TableCell>
                    <TableCell>Commit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLayout === 'grouped' || classSelected
                    ? renderGroupedBody()
                    : displayStudents.map((s) => renderDesktopRow(s))}
                </TableBody>
              </Table>
            </TableContainer>
            {!classSelected && (
              <TablePagination
                component="div"
                count={orderedStudents.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            )}
          </>
        )}
        {!loading && students.length === 0 && currentSession && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {classFilter === '__unassigned__'
              ? 'No unassigned active students.'
              : classSelected
                ? 'No active students in this class.'
                : 'No active students.'}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: { xs: 56, md: 0 },
          p: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: hasDirty ? 'warning.main' : 'divider',
          zIndex: 9,
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          size="large"
          color={hasDirty ? 'warning' : 'primary'}
          disabled={!hasDirty || saving || loading}
          onClick={handleSaveAttendance}
          sx={{ minHeight: 48, flex: touchMode && canNavClasses ? '1 1 140px' : '1 1 100%' }}
        >
          {saving
            ? 'Saving…'
            : hasDirty
              ? `Save (${dirtyCount})`
              : 'All marks saved'}
        </Button>
        {touchMode && canNavClasses && (
          <Button
            variant="outlined"
            size="large"
            disabled={saving || loading || currentClassIndex >= classNavOptions.length - 1}
            onClick={handleSaveAndNextClass}
            sx={{ minHeight: 48, flex: '1 1 160px' }}
          >
            {hasDirty ? 'Save & next class' : 'Next class'}
          </Button>
        )}
      </Box>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Daily absent report — {date}</DialogTitle>
        <DialogContent>
          {absentReport && (
            <Box>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Absent ({absentReport.absent?.length ?? 0})</Typography>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Student</TableCell><TableCell>District</TableCell><TableCell>Service type</TableCell><TableCell>Absence reason</TableCell><TableCell>Contact</TableCell></TableRow></TableHead>
                <TableBody>
                  {(absentReport.absent || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{row.district}</TableCell>
                      <TableCell>{row.service_type}</TableCell>
                      <TableCell>{row.absence_reason}</TableCell>
                      <TableCell>{row.mother_cell || row.father_cell || row.home_phone || row.email || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Missing attendance entry ({absentReport.missing_attendance_entry?.length ?? 0})</Typography>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Student</TableCell><TableCell>District</TableCell><TableCell>Service type</TableCell><TableCell>Contact</TableCell></TableRow></TableHead>
                <TableBody>
                  {(absentReport.missing_attendance_entry || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{row.district}</TableCell>
                      <TableCell>{row.service_type}</TableCell>
                      <TableCell>{row.mother_cell || row.father_cell || row.home_phone || row.email || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Close</Button>
          {absentReport && <Button onClick={exportDailyAbsentCsv}>Export CSV</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendancePage;
